import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
    theme: Theme;
    setTheme: (theme: Theme) => void;
    resolvedTheme: 'light' | 'dark';
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

interface ThemeProviderProps {
    children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
    const [theme, setTheme] = useState<Theme>('system');
    const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>('light');

    // Get system preference
    const getSystemTheme = (): 'light' | 'dark' => {
        if (typeof window !== 'undefined' && window.matchMedia) {
            try {
                const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
                return mediaQuery && mediaQuery.matches ? 'dark' : 'light';
            } catch (error) {
                console.warn('Failed to get system theme preference:', error);
                return 'light';
            }
        }
        return 'light';
    };

    // Resolve the actual theme based on user preference and system preference
    const resolveTheme = (userTheme: Theme): 'light' | 'dark' => {
        if (userTheme === 'system') {
            return getSystemTheme();
        }
        return userTheme;
    };

    // Apply theme to document
    const applyTheme = (resolvedTheme: 'light' | 'dark') => {
        const root = document.documentElement;

        if (resolvedTheme === 'dark') {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }
    };

    // Load theme from localStorage on mount
    useEffect(() => {
        const savedTheme = localStorage.getItem('theme') as Theme;
        if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
            setTheme(savedTheme);
        }
    }, []);

    // Update resolved theme when theme or system preference changes
    useEffect(() => {
        const newResolvedTheme = resolveTheme(theme);
        setResolvedTheme(newResolvedTheme);
        applyTheme(newResolvedTheme);
    }, [theme]);

    // Listen for system theme changes
    useEffect(() => {
        if (theme === 'system' && typeof window !== 'undefined' && window.matchMedia) {
            try {
                const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

                if (mediaQuery && mediaQuery.addEventListener) {
                    const handleChange = () => {
                        const newResolvedTheme = resolveTheme(theme);
                        setResolvedTheme(newResolvedTheme);
                        applyTheme(newResolvedTheme);
                    };

                    mediaQuery.addEventListener('change', handleChange);
                    return () => {
                        if (mediaQuery && mediaQuery.removeEventListener) {
                            mediaQuery.removeEventListener('change', handleChange);
                        }
                    };
                }
            } catch (error) {
                console.warn('Failed to set up system theme listener:', error);
            }
        }
    }, [theme]);

    // Save theme preference to localStorage
    const handleSetTheme = (newTheme: Theme) => {
        setTheme(newTheme);
        localStorage.setItem('theme', newTheme);
    };

    const value: ThemeContextType = {
        theme,
        setTheme: handleSetTheme,
        resolvedTheme,
    };

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};
